import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Space, Descriptions, message, Tabs, Dropdown, Popconfirm, Switch, Checkbox, Empty, Alert, Divider, Spin } from 'antd';
import { EyeOutlined, SyncOutlined, MoreOutlined, PauseCircleOutlined, PlayCircleOutlined, StopOutlined, ReloadOutlined, SafetyCertificateOutlined, DownloadOutlined, CheckCircleOutlined, SettingOutlined } from '@ant-design/icons';
import sslService from '../../services/sslService';
import AcmeSubscriptions from './AcmeSubscriptions';

const { Option } = Select;
const { TextArea } = Input;

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

const SslOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [filters, setFilters] = useState({});
  const [detailModal, setDetailModal] = useState({ visible: false, order: null });
  const [actionModal, setActionModal] = useState({ visible: false, type: '', order: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionForm] = Form.useForm();
  const [validationInfo, setValidationInfo] = useState({ loading: false, data: null, error: '' });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async (page = 1, pageSize = 50, filterParams = {}) => {
    setLoading(true);
    try {
      const params = { page, per_page: pageSize };
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.provider) params.provider = filterParams.provider;
      const response = await sslService.getOrders(params);
      if (response.success) {
        setOrders(response.data);
        setPagination({
          current: response.meta?.current_page || page,
          pageSize: response.meta?.per_page || pageSize,
          total: response.meta?.total || response.data?.length || 0,
        });
      }
    } catch (error) {
      message.error('加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination, tableFilters) => {
    const newFilters = {
      status: tableFilters.status?.[0],
      provider: tableFilters.provider?.[0],
    };
    setFilters(newFilters);
    fetchOrders(newPagination.current, newPagination.pageSize, newFilters);
  };

  const handleViewDetail = (order) => {
    setDetailModal({ visible: true, order });
  };

  const handleAction = (type, order) => {
    actionForm.resetFields();
    setValidationInfo({ loading: false, data: null, error: '' });
    setActionModal({ visible: true, type, order });
    if (['validate', 'change_validation'].includes(type)) {
      fetchValidationInfo(order.id);
    }
  };

  const fetchValidationInfo = async (orderId) => {
    setValidationInfo({ loading: true, data: null, error: '' });
    try {
      const response = await sslService.getValidationStatus(orderId);
      if (response.success) {
        setValidationInfo({ loading: false, data: response.data, error: '' });
      } else {
        setValidationInfo({ loading: false, data: null, error: response.error || '获取验证信息失败' });
      }
    } catch (error) {
      setValidationInfo({ loading: false, data: null, error: error.message || '获取验证信息失败' });
    }
  };

  const executeAction = async () => {
    setActionLoading(true);
    try {
      const { type, order } = actionModal;
      const values = await actionForm.validateFields().catch(() => ({}));
      let response;

      switch (type) {
        case 'configure':
          response = await sslService.configureOrder(order.id, values.domain, values.san_domains || []);
          break;
        case 'validate':
          response = await sslService.submitValidation(order.id, values.validation_method || 'dns');
          break;
        case 'download':
          response = await sslService.downloadCertificate(order.id);
          break;
        case 'renew':
          response = await sslService.renewOrder(order.id, values.free_renewal || false);
          break;
        case 'cancel':
          response = await sslService.cancelOrder(order.id);
          break;
        case 'suspend':
          response = await sslService.suspendOrder(order.id, values.reason || '');
          break;
        case 'resume':
          response = await sslService.resumeOrder(order.id);
          break;
        case 'revoke':
          response = await sslService.revokeOrder(order.id, values.reason || '');
          break;
        case 'reissue':
          response = await sslService.reissueOrder(order.id);
          break;
        case 'refresh':
          response = await sslService.refreshOrderStatus(order.id);
          break;
        case 'change_validation':
          response = await sslService.changeValidationMethod(order.id, values.validation_method);
          break;
        default:
          throw new Error('未知操作');
      }

      if (response.success) {
        message.success('操作成功');
        setActionModal({ visible: false, type: '', order: null });
        fetchOrders(pagination.current, pagination.pageSize, filters);
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      pending_validation: 'gold',
      processing: 'blue',
      active: 'green',
      expired: 'red',
      revoked: 'red',
      failed: 'red',
      suspended: 'volcano',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: '待处理',
      pending_validation: '待验证',
      processing: '处理中',
      active: '生效中',
      expired: '已过期',
      revoked: '已吊销',
      failed: '失败',
      suspended: '已暂停',
      cancelled: '已取消',
    };
    return texts[status] || status;
  };

  const getActionItems = (record) => {
    const items = [];
    const status = record.status;

    // 配置域名/SAN（为待处理订单代配置）
    if (['pending', 'pending_validation'].includes(status)) {
      items.push({ key: 'configure', label: '配置域名/SAN', icon: <SettingOutlined /> });
    }

    // 验证相关操作
    if (['pending', 'pending_validation'].includes(status)) {
      items.push({ key: 'validate', label: '发起验证', icon: <CheckCircleOutlined /> });
      items.push({ key: 'change_validation', label: '更改验证方式', icon: <SyncOutlined /> });
    }

    // 下载证书
    if (status === 'active') {
      items.push({ key: 'download', label: '下载证书', icon: <DownloadOutlined /> });
    }

    // 续费
    if (['active', 'expired'].includes(status)) {
      items.push({ key: 'renew', label: '续费', icon: <SafetyCertificateOutlined /> });
    }

    // 暂停/恢复
    if (status === 'active') {
      items.push({ key: 'suspend', label: '暂停', icon: <PauseCircleOutlined /> });
    }
    if (status === 'suspended') {
      items.push({ key: 'resume', label: '恢复', icon: <PlayCircleOutlined /> });
    }

    // 吊销
    if (status === 'active') {
      items.push({ key: 'revoke', label: '吊销', icon: <StopOutlined />, danger: true });
    }

    // 重新签发
    if (['active', 'revoked'].includes(status)) {
      items.push({ key: 'reissue', label: '重新签发', icon: <ReloadOutlined /> });
    }

    // 取消
    if (['pending', 'pending_validation', 'processing'].includes(status)) {
      items.push({ key: 'cancel', label: '取消订单', icon: <StopOutlined />, danger: true });
    }

    // 刷新状态
    items.push({ key: 'refresh', label: '刷新状态', icon: <SyncOutlined /> });

    return items;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '用户',
      dataIndex: 'user',
      key: 'user',
      render: (user) => user ? `${user.username || user.email || user.name || '-'} (ID: ${user.id})` : '-',
    },
    {
      title: '域名',
      key: 'domain',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.domain}</div>
          {ensureArray(record.san_domains).length > 0 && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              +{ensureArray(record.san_domains).length} 个SAN域名
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      filters: [
        { text: 'Let\'s Encrypt', value: 'letsencrypt' },
        { text: 'ZeroSSL', value: 'zerossl' },
        { text: 'GoGetSSL', value: 'gogetssl' },
        { text: 'Certum', value: 'certum' },
      ],
      render: (text) => {
        const names = {
          letsencrypt: 'Let\'s Encrypt',
          zerossl: 'ZeroSSL',
          gogetssl: 'GoGetSSL',
          certum: 'Certum',
          acme: 'ACME',
          manual: 'Manual Upload',
        };
        return <Tag color="blue">{names[text] || text}</Tag>;
      },
    },
    {
      title: '产品',
      dataIndex: 'product',
      key: 'product',
      render: (product) => product ? product.name : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: '待处理', value: 'pending' },
        { text: '待验证', value: 'pending_validation' },
        { text: '处理中', value: 'processing' },
        { text: '生效中', value: 'active' },
        { text: '已过期', value: 'expired' },
        { text: '已吊销', value: 'revoked' },
        { text: '已暂停', value: 'suspended' },
        { text: '失败', value: 'failed' },
      ],
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date) => date ? new Date(date).toLocaleDateString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          <Dropdown
            menu={{
              items: getActionItems(record),
              onClick: ({ key }) => handleAction(key, record),
            }}
            trigger={['click']}
          >
            <Button type="link" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const getActionModalTitle = () => {
    const titles = {
      configure: '配置域名/SAN',
      validate: '发起域名验证',
      download: '下载证书',
      renew: '续费证书',
      cancel: '取消订单',
      suspend: '暂停订单',
      resume: '恢复订单',
      revoke: '吊销证书',
      reissue: '重新签发证书',
      refresh: '刷新状态',
      change_validation: '更改验证方式',
    };
    return titles[actionModal.type] || '操作';
  };

  const renderValidationInfo = () => {
    const { order } = actionModal;
    const vData = validationInfo.data?.validation_data;
    return (
      <>
        <Divider style={{ margin: '12px 0' }}>验证信息</Divider>
        {validationInfo.loading ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin /></div>
        ) : validationInfo.error ? (
          <Alert type="warning" showIcon message={validationInfo.error} />
        ) : validationInfo.data ? (
          <>
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="本地状态">
                <Tag color={getStatusColor(validationInfo.data.local_status)}>
                  {getStatusText(validationInfo.data.local_status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Provider状态">
                {validationInfo.data.provider_status || 'unknown'}
              </Descriptions.Item>
            </Descriptions>
            {(order?.validation_token || order?.validation_value) && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="DNS验证记录"
                description={
                  <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="记录类型">TXT</Descriptions.Item>
                    <Descriptions.Item label="主机记录">
                      <code>_acme-challenge.{order?.domain}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="记录值">
                      <code>{order?.validation_token || order?.validation_value || '(待生成)'}</code>
                    </Descriptions.Item>
                  </Descriptions>
                }
              />
            )}
            {vData ? (
              <div>
                <div style={{ marginBottom: 4, color: '#666' }}>Provider 验证数据：</div>
                <Input.TextArea
                  readOnly
                  rows={5}
                  value={typeof vData === 'string' ? vData : JSON.stringify(vData, null, 2)}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>
            ) : null}
          </>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无验证信息" />
        )}
      </>
    );
  };

  const renderActionModalContent = () => {
    const { type, order } = actionModal;

    switch (type) {
      case 'configure':
        return (
          <Form form={actionForm} layout="vertical">
            <Form.Item
              name="domain"
              label="主域名"
              initialValue={order?.domain}
              rules={[{ required: true, message: '请输入主域名' }]}
            >
              <Input placeholder="example.com" />
            </Form.Item>
            <Form.Item name="san_domains" label="SAN 域名（可选）" initialValue={ensureArray(order?.san_domains)}>
              <Select mode="tags" tokenSeparators={[',', ' ']} placeholder="输入后回车添加，如 www.example.com" />
            </Form.Item>
          </Form>
        );
      case 'validate':
      case 'change_validation':
        return (
          <>
            <Form form={actionForm} layout="vertical">
              <Form.Item name="validation_method" label="验证方式" initialValue="dns">
                <Select>
                  <Option value="dns">DNS验证</Option>
                  <Option value="http">HTTP验证</Option>
                  <Option value="email">邮箱验证</Option>
                </Select>
              </Form.Item>
            </Form>
            {renderValidationInfo()}
          </>
        );
      case 'renew':
        return (
          <Form form={actionForm} layout="vertical">
            <p>确定要续费订单 #{order?.id} 吗？</p>
            <Form.Item name="free_renewal" valuePropName="checked" initialValue={false}>
              <Checkbox>免费续费（管理员特权）</Checkbox>
            </Form.Item>
          </Form>
        );
      case 'suspend':
      case 'revoke':
        return (
          <Form form={actionForm} layout="vertical">
            <Form.Item name="reason" label="原因">
              <TextArea rows={3} placeholder={`请输入${type === 'suspend' ? '暂停' : '吊销'}原因`} />
            </Form.Item>
          </Form>
        );
      case 'cancel':
      case 'resume':
      case 'reissue':
      case 'refresh':
      case 'download':
        return <p>确定要执行此操作吗？订单ID: #{order?.id}，域名: {order?.domain}</p>;
      default:
        return null;
    }
  };

  return (
    <div>
      <Card
        title="SSL订单管理"
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => fetchOrders(pagination.current, pagination.pageSize, filters)} loading={loading}>
            刷新
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={orders}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无 SSL 订单" /> }}
          onChange={handleTableChange}
        />
      </Card>

      <AcmeSubscriptions />

      {/* 详情模态框 */}
      <Modal
        title="SSL订单详情"
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, order: null })}
        footer={null}
        width={900}
      >
        {detailModal.order && (
          <Tabs
            items={[
              {
                key: 'basic',
                label: '基本信息',
                children: (
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="订单ID">{detailModal.order.id}</Descriptions.Item>
                    <Descriptions.Item label="用户">
                      {detailModal.order.user ? `${detailModal.order.user.username || detailModal.order.user.email || detailModal.order.user.name || '-'} (ID: ${detailModal.order.user.id})` : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Provider">
                      <Tag color="blue">{detailModal.order.provider}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="产品">
                      {detailModal.order.product ? detailModal.order.product.name : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="主域名">{detailModal.order.domain}</Descriptions.Item>
                    <Descriptions.Item label="状态">
                      <Tag color={getStatusColor(detailModal.order.status)}>
                        {getStatusText(detailModal.order.status)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="SAN域名" span={2}>
                      {ensureArray(detailModal.order.san_domains).length > 0
                        ? ensureArray(detailModal.order.san_domains).join(', ')
                        : '无'}
                    </Descriptions.Item>
                    <Descriptions.Item label="颁发时间">
                      {detailModal.order.issued_at ? new Date(detailModal.order.issued_at).toLocaleString('zh-CN') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="过期时间">
                      {detailModal.order.expires_at ? new Date(detailModal.order.expires_at).toLocaleString('zh-CN') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="有效期">{detailModal.order.validity_days || '-'} 天</Descriptions.Item>
                    <Descriptions.Item label="自动续期">
                      <Tag color={detailModal.order.auto_renew ? 'green' : 'default'}>
                        {detailModal.order.auto_renew ? '是' : '否'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: 'cert',
                label: '证书信息',
                children: (
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label="颁发者" span={2}>{detailModal.order.issuer || '-'}</Descriptions.Item>
                    <Descriptions.Item label="序列号" span={2}>{detailModal.order.serial_number || '-'}</Descriptions.Item>
                    <Descriptions.Item label="SHA256指纹" span={2}>
                      <code style={{ fontSize: '12px' }}>{detailModal.order.fingerprint_sha256 || '-'}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="SHA1指纹" span={2}>
                      <code style={{ fontSize: '12px' }}>{detailModal.order.fingerprint_sha1 || '-'}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="Common Name">{detailModal.order.common_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Organization">{detailModal.order.organization || '-'}</Descriptions.Item>
                    <Descriptions.Item label="证书内容" span={2}>
                      {detailModal.order.certificate ? (
                        <TextArea
                          value={detailModal.order.certificate}
                          rows={6}
                          readOnly
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      ) : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CA Bundle" span={2}>
                      {detailModal.order.ca_bundle ? (
                        <TextArea
                          value={detailModal.order.ca_bundle}
                          rows={4}
                          readOnly
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      ) : '-'}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: 'timeline',
                label: '时间线',
                children: (
                  <Descriptions bordered column={1}>
                    <Descriptions.Item label="创建时间">
                      {new Date(detailModal.order.created_at).toLocaleString('zh-CN')}
                    </Descriptions.Item>
                    <Descriptions.Item label="更新时间">
                      {new Date(detailModal.order.updated_at).toLocaleString('zh-CN')}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
            ]}
          />
        )}
      </Modal>

      {/* 操作模态框 */}
      <Modal
        title={getActionModalTitle()}
        open={actionModal.visible}
        onOk={executeAction}
        onCancel={() => setActionModal({ visible: false, type: '', order: null })}
        confirmLoading={actionLoading}
        okText="确定"
        cancelText="取消"
      >
        {renderActionModalContent()}
      </Modal>
    </div>
  );
};

export default SslOrders;
